import { css, StyleSheet } from 'aphrodite';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { FormFieldType } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { AnimatedLionGraphic } from '../../components/svgs/AnimatedLionGraphic';
import { HexGraphic } from '../../components/svgs/HexGraphic';
import { FormFieldResolvers } from '../../viewModels/FormFieldResolvers';
import { DealField } from './DealField';
import type { CreateDealResult, DealFieldDef } from './types';
import { useCreateDeal } from './useCreateDeal';
import { useDealForm } from './useDealForm';
import { useUpdateDeal } from './useUpdateDeal';

/**
 * Props for the `DealForm` orchestrator.
 *
 * `initialValues` lets the wrap-up flow seed obvious fields (e.g. a
 * description copied from the wrap-up note); `customProperties` is a
 * pass-through for context the legacy `CustomFormViewModel.save` sends
 * along (decisionMaker, etc.). `onCreated` fires with the
 * `CreateDealResult` once the submit resolves successfully — the same
 * callback covers both Create and Update paths so the wrap-up flow can
 * react identically to either.
 *
 * When `dealId` is provided the form switches to Update mode: schema is
 * fetched from `UpdateDeal/{dealId}` (with current values pre-populated),
 * the header reads "Update Deal", and submit PUTs to the same URL. Without
 * `dealId` the form behaves as before — Create Deal via POST.
 */
export type DealFormProps = {
	companyId: string;
	/**
	 * When set, the form runs in Update mode against the existing deal —
	 * mirrors the legacy `lead.loadDealForm(dealId)` path used for the
	 * `NewDemo` / `AppointmentRescheduled` outcomes.
	 */
	dealId?: string;
	/** Optional pre-fill of values, e.g. from the wrap-up step. */
	initialValues?: Record<string, unknown>;
	/** Optional pass-through to useCreateDeal / useUpdateDeal (e.g. decisionMaker). */
	customProperties?: Record<string, unknown>;
	onCancel?: () => void;
	onCreated?: (result: CreateDealResult) => void;
};

/**
 * Orchestrates the Create-Deal form.
 *
 * Loads the form schema via `useDealForm`, tracks per-field values in
 * local state seeded from each field's server-supplied `initialValue`
 * (parent-supplied `initialValues` take precedence), validates required
 * fields on Create, and POSTs via `useCreateDeal`. Loading and error
 * states render inline.
 */
export const DealForm: FC<DealFormProps> = ({
	companyId,
	dealId,
	initialValues,
	customProperties,
	onCancel,
	onCreated,
}) => {
	const isUpdate = !!dealId;
	const { config, isLoading, error: loadError } = useDealForm(companyId, dealId);
	const createDeal = useCreateDeal();
	const updateDeal = useUpdateDeal();
	// Pick whichever mutation is active for this mode so loading state and the
	// disabled gating below can read from a single object. The other mutation
	// stays idle and never fires.
	const activeMutation = isUpdate ? updateDeal : createDeal;
	const userSession = useUserSession();

	const [values, setValues] = useState<Record<string, unknown>>(initialValues ?? {});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Mirrors legacy `meetingSchedulerState.schedulingType === Automatic`
	// behaviour: once the rep clicks "Find Demo Spot" and the booking URL
	// resolves, the demo-perform-date and demo-performer fields are hidden
	// because the server populates them when the rep returns. Stays `false`
	// for the manual flow.
	const [schedulerEngaged, setSchedulerEngaged] = useState(false);

	// Hydrate values once the config arrives. We only fill fields that
	// haven't already been set by the parent (initialValues wins) — and we
	// only mutate when something actually changed, to avoid a render loop.
	useEffect(() => {
		if (!config) return;
		setValues(prev => {
			const next = { ...prev };
			let changed = false;
			for (const f of config.fields) {
				if (next[f.name] === undefined && f.initialValue !== undefined) {
					next[f.name] = f.initialValue;
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [config]);

	const handleFieldChange = useCallback((name: string, next: unknown) => {
		setValues(prev => ({ ...prev, [name]: next }));
		// Clear the field-level error as soon as the user edits the field —
		// matches the legacy form behaviour and avoids stale "is required"
		// messages while typing.
		setErrors(prev => {
			if (!prev[name]) return prev;
			const { [name]: _removed, ...rest } = prev;
			return rest;
		});
	}, []);

	// Resolve the contact id off the form's `Contact`-typed field (the
	// legacy form names this "Associated Contact"). The MeetingScheduler
	// field needs this so it can POST a one-shot demo-link request keyed on
	// the contact. We pick the first Contact-typed field — there is at most
	// one in the Create Deal flow.
	const associatedContactId = useMemo<string | undefined>(() => {
		if (!config) return undefined;
		const contactField = config.fields.find(f => f.fieldType === FormFieldType.Contact);
		if (!contactField) return undefined;
		const v = values[contactField.name];
		return typeof v === 'string' && v ? v : undefined;
	}, [config, values]);

	// Names of the demo-perform-date / demo-performer fields, resolved via
	// the same coffee-key mappings the legacy form uses. We hide these fields
	// once the auto scheduler has been engaged, because the server populates
	// them when the rep returns from booking — matching legacy behaviour.
	const visibleFields = useMemo<DealFieldDef[]>(() => {
		if (!config) return [];
		if (!schedulerEngaged) return config.fields;
		const demoPerformDateName = FormFieldResolvers.getDemoPerformDateField(userSession);
		const demoPerformerName = FormFieldResolvers.getDemoPerformerField(userSession);
		return config.fields.filter(f => f.name !== demoPerformDateName && f.name !== demoPerformerName);
	}, [config, schedulerEngaged, userSession]);

	const handleSchedulerEngaged = useCallback(() => {
		setSchedulerEngaged(true);
	}, []);

	const handleSchedulerDisengaged = useCallback(() => {
		setSchedulerEngaged(false);
	}, []);

	const handleCreate = useCallback(async () => {
		if (!config) return;

		const missing: Record<string, string> = {};
		const orderedMissingNames: string[] = [];
		for (const f of config.fields) {
			if (f.isHidden) continue;
			const required = f.isOptional !== true;
			if (!required) continue;
			const v = values[f.name];
			if (v === undefined || v === null || v === '') {
				missing[f.name] = `${f.label} is required`;
				orderedMissingNames.push(f.name);
			}
		}
		if (orderedMissingNames.length > 0) {
			setErrors(missing);
			// Match legacy `DealDrawerContent.onActionClick` behaviour: scroll
			// the first invalid field into view so the rep can correct it.
			// Pulled out of render so SSR/JSDOM `document` access is gated by
			// the actual user click; the field id is the stable
			// `deal-field-${name}` set by `DealField`. JSDOM does not
			// implement `scrollIntoView`, hence the `typeof` guard on the
			// function itself.
			const firstName = orderedMissingNames[0];
			if (typeof document !== 'undefined') {
				const el = document.getElementById(`deal-field-${firstName}`);
				if (el && typeof el.scrollIntoView === 'function') {
					el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
			return;
		}

		// Fork on mode: Update PUTs to UpdateDeal/{dealId}; Create POSTs to
		// CreateDeal. Both resolve to the same `CreateDealResult` shape so
		// the `onCreated` callback works for either path.
		const result = isUpdate
			? await updateDeal.mutateAsync({
					companyId,
					dealId: dealId as string,
					payload: { values, customProperties },
				})
			: await createDeal.mutateAsync({
					companyId,
					payload: { values, customProperties },
				});
		onCreated?.(result);
	}, [config, values, customProperties, companyId, dealId, isUpdate, createDeal, updateDeal, onCreated]);

	if (isLoading) {
		return (
			<div className={css(styles.state)} aria-live='polite'>
				Loading deal form…
			</div>
		);
	}
	if (loadError) {
		console.error('Deal form load error:', loadError);
		return <div className={css(styles.state)}>Could not load the deal form.</div>;
	}
	if (!config) {
		return null;
	}

	// Match legacy `DealDrawerContent` button + header copy
	// (`aida/components/Queue/DealDrawerContent/index.tsx:131-148`): label
	// flips between Create / Update (and the busy variants) based on
	// whether a `dealId` is in play.
	const headerTitle = isUpdate ? 'Update Deal' : 'Create Deal';
	const submitLabel = activeMutation.isPending
		? isUpdate
			? 'Updating…'
			: 'Creating…'
		: isUpdate
			? 'Update'
			: 'Create';

	return (
		<div className={css(styles.root)}>
			<header className={css(styles.head)}>
				<div className={css(styles.hexWrap)}>
					<HexGraphic animate={false} selected={true} className={css(styles.hex)} />
					<AnimatedLionGraphic animate={false} className={css(styles.lion)} />
				</div>
				<span className={css(styles.title)}>{headerTitle}</span>
			</header>
			<div className={css(styles.fields)}>
				{visibleFields.map(f => (
					<DealField
						key={f.name}
						field={f}
						value={values[f.name]}
						onChange={next => handleFieldChange(f.name, next)}
						error={errors[f.name]}
						disabled={activeMutation.isPending}
						companyId={companyId}
						associatedContactId={associatedContactId}
						onSchedulerEngaged={handleSchedulerEngaged}
						onSchedulerDisengaged={handleSchedulerDisengaged}
					/>
				))}
			</div>
			<footer className={css(styles.actions)}>
				{/*
				 * Legacy ordering: primary action first (Create / Update), Cancel
				 * second. The legacy CTA bar centred its children; we keep the
				 * focused-view's right-aligned bar but match legacy ordering so
				 * the primary action is the visually leading control.
				 */}
				<Button onClick={handleCreate} disabled={activeMutation.isPending}>
					{submitLabel}
				</Button>
				{onCancel && (
					<Button variant='outline' onClick={onCancel} disabled={activeMutation.isPending}>
						Cancel
					</Button>
				)}
			</footer>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
		minHeight: 60,
	},
	hexWrap: {
		position: 'relative',
		width: 42,
		height: 42,
		flexShrink: 0,
	},
	hex: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		height: '100%',
		width: '100%',
	},
	lion: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		height: '70%',
		width: '70%',
	},
	title: {
		fontSize: '1.25rem',
		fontWeight: 600,
		color: 'var(--accent)',
	},
	fields: {
		flex: 1,
		overflowY: 'auto',
		// Match legacy `DealDrawerContent.dealForm` density: 20px top, 30px
		// horizontal, 0 bottom (the last field's spacing is owned by the
		// field itself in legacy; we approximate with a uniform gap).
		padding: '20px 30px 0',
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 8,
		padding: '12px 18px',
		borderTop: '1px solid var(--border)',
	},
	state: {
		padding: '14px 18px',
		color: 'var(--ink-muted)',
		fontSize: 13,
	},
});
